// WordPress Search Client for vue-instantsearch
// This adapter allows vue-instantsearch to work with WordPress backend
// while maintaining compatibility for future Algolia integration
class WordPressSearchClient {
	constructor(ajaxUrl, postType = 'library') {
		this.ajaxUrl = ajaxUrl;
		this.postType = postType;
		this.cachedFacets = null;
		this.lastFilterState = null;
	}

	async search(requests) {
		// Only process the first request (main search), ignore facet population requests
		const mainResult = await this.performSearch(requests[0]);
		// For additional requests, return empty results to avoid unnecessary backend calls
		const results = [mainResult];
		for (let i = 1; i < requests.length; i++) {
			// results.push(this.getEmptyResponse());
		}
		
		return { results };
	}

	async performSearch(request) {
		const { query, params, indexName } = request;
		
		const searchQuery = query || params.query || '';
		
		const sortBy = indexName && indexName.includes(':') ? indexName : '';

		// Check if filters have changed to invalidate facet cache
		const currentFilterState = JSON.stringify({
			facetFilters: params.facetFilters,
			numericFilters: params.numericFilters,
			query: searchQuery,
			postType: this.postType
		});
		
		if (this.lastFilterState !== currentFilterState) {
			this.cachedFacets = null;
			this.lastFilterState = currentFilterState;
		}

		const searchParams = {
			action: 'cr_search_posts',
			post_type: this.postType,
			s: searchQuery,
			page: (params.page || 0) + 1,
			per_page: params.hitsPerPage || 6,
			favorites: params.favorites,
			...this.buildFilters(params.facetFilters, params.numericFilters),
			...this.buildSortParams(sortBy)
		};

		// console.log('Search params being sent:', searchParams);

		try {
			const queryString = new URLSearchParams(searchParams).toString();
			const response = await fetch(`${this.ajaxUrl}?${queryString}`, {
				method: 'GET'
			});

			const data = await response.json();
			
			if (data.success) {
				return this.formatResponse(data.data, searchParams);
			} else {
				throw new Error(data.data || 'Search failed');
			}
		} catch (error) {
			console.error('WordPress search error:', error);
			return this.getEmptyResponse();
		}
	}

	buildFilters(facetFilters = [], numericFilters = []) {
		const filters = {};
		
		// Process facet filters
		facetFilters.forEach(filter => {
			if (Array.isArray(filter)) {
				// OR filters (array of strings)
				filter.forEach(f => {
					const [key, value] = f.split(':');
					if (!filters[key]) filters[key] = [];
					filters[key].push(value);
				});
			} else {
				// Single filter (string)
				const [key, value] = filter.split(':');
				if (!filters[key]) filters[key] = [];
				filters[key].push(value);
			}
		});

		// Process numeric filters
		numericFilters.forEach(filter => {
			const match = filter.match(/(\w+)\s*([<>=]+)\s*(\d+)/);
			if (match) {
				const [, key, operator, value] = match;
				filters[`${key}_${operator}`] = value;
			}
		});

		return filters;
	}

	buildSortParams(sortBy) {
		if (!sortBy) return {};
		
		// Parse sortBy format: "field:direction"
		const [field, direction] = sortBy.split(':');
		
		return {
			sort_by: field,
			sort_order: direction || 'asc'
		};
	}

	formatResponse(data, searchParams) {
		const hits = data.posts || [];

		const result = {
			hits,
			nbHits: data.total || 0,
			page: (searchParams.page || 1) - 1,
			nbPages: data.pages || 0,
			hitsPerPage: searchParams.per_page || 6,
			processingTimeMS: data.processing_time || 1,
			query: searchParams.s || '',
			params: searchParams
		};

		// Handle facet caching
		if (data.facets && Object.keys(data.facets).length > 0) {
			// Cache facets from backend response
			this.cachedFacets = this.transformFacets(data.facets);
			result.facets = this.cachedFacets;
		} else if (this.cachedFacets) {
			// Use cached facets if no facets in response
			result.facets = this.cachedFacets;
		}

		return result;
	}

	transformFacets(facets) {
		const transformedFacets = {};
		
		// Transform each facet field
		Object.keys(facets).forEach(key => {
			// Transform other facets to Algolia format
			transformedFacets[key] = {};
			facets[key].forEach(item => {
				transformedFacets[key][item.value] = item.count;
			});
		});

		return transformedFacets;
	}

	getEmptyResponse() {
		return {
			hits: [],
			nbHits: 0,
			page: 0,
			nbPages: 0,
			hitsPerPage: 6,
			processingTimeMS: 1,
			query: '',
			params: '',
			facets: {},
			facets_stats: {}
		};
	}
}

export default WordPressSearchClient;
